import { hasOwn } from "@api";
import type { ISettingMaster, ISettingValueMaster } from "./types/setting";

export type ISettingDefinistion = Record<string, { domain: string; settingType: string }>;

export const settingsDefinitions = {
  statusCandidate: {
    domain: "status",
    settingType: "candidate",
  },
  statusTodoType: { domain: "status", settingType: "todo_type" },
  settingsSources: { domain: "settings", settingType: "sources" },
  statusCerStatus: { domain: "status", settingType: "cerStatus" },
  statusVehicle: { domain: "status", settingType: "vehicle" },
  statusSourceOffer: {
    settingType: "source_offer",
    domain: "status",
  },
  settingsContact: { domain: "settings", settingType: "contactOption" },
  statusSocialTag: {
    settingType: "social_tag",
    domain: "status",
  },
  statusCgu: { domain: "status", settingType: "cgu" },
  settingsContractTypes: {
    domain: "settings",
    settingType: "contract-types",
  },
  settingsOfferOrigin: {
    settingType: "offer_origin",
    domain: "settings",
  },
  settingsOfferSourceOrigin: {
    settingType: "offer_source_origin",
    domain: "settings",
  },
  settingsApplySourceOrigin: {
    settingType: "apply_source_origin",
    domain: "settings",
  },
  settingsSourceSocialOffer: { domain: "settings", settingType: "source_social_offer" },
  settingsSourceTrainingOffer: { domain: "settings", settingType: "source_training_offer" },
  settingsEqfTrainingLevel: { domain: "settings", settingType: "eqfTrainingLevel" },
  settingsCoachingStep: {
    settingType: "coachingStep",
    domain: "settings",
  },
  settingsAdministrativeExit: {
    settingType: "administrative_exit",
    domain: "settings",
  },
  settingsNegativeExitReason: {
    settingType: "negative_exit_reason",
    domain: "settings",
  },
  settingsBetterRight: {
    settingType: "better_right",
    domain: "settings",
  },
  settingsDeleteReason: {
    settingType: "delete_reason",
    domain: "settings",
  },
  actionChannel: {
    settingType: "action_channel",
    domain: "action",
  },
  settingsActivityArea: {
    settingType: "activityArea",
    domain: "settings",
  },
  supportCourseStatus: {
    domain: "workshop",
    settingType: "supportCourseStatuses",
  },
  workshopsType: {
    domain: "settings",
    settingType: "workshops",
  },
  permissionsThemes: {
    domain: "settings",
    settingType: "permissionTheme",
  },
  vehicles: {
    domain: "status",
    settingType: "vehicle",
  },
  driverLicenses: {
    domain: "settings",
    settingType: "driverLicenses",
  },
  cerTypes: {
    domain: "settings",
    settingType: "type_de_cer",
  },
  financingModes: {
    settingType: "financingModes",
    domain: "settings",
  },
} as const satisfies ISettingDefinistion;

export const filterSettingValues = (domain: string, settingType: string, settings: ISettingMaster[]) => {
  const setting = settings.find((s) => s.domain === domain && s.settingType === settingType);
  return setting?.values ? setting.values : null;
};

export type FilteredSettingValues = Partial<Record<keyof typeof settingsDefinitions, ISettingValueMaster[]>>;

export const returnFilteredSettingValues = (resultInitSettings: ISettingMaster[]) => {
  const returnSettings: FilteredSettingValues = {};
  for (const key in settingsDefinitions) {
    if (hasOwn(settingsDefinitions, key)) {
      returnSettings[key] =
        filterSettingValues(
          settingsDefinitions[key].domain,
          settingsDefinitions[key].settingType,
          resultInitSettings
        ) ?? undefined;
    }
  }
  return returnSettings;
};

export const filterSetting = (domain: string, settingType: string, settings: ISettingMaster[]) => {
  const setting = settings.find((s) => s.domain === domain && s.settingType === settingType);
  return setting;
};

export type FilteredSettings = Partial<Record<keyof typeof settingsDefinitions, ISettingMaster>>;

export const returnFilteredSettings = <T extends ISettingDefinistion>(
  settingsDefinitions: T,
  resultInitSettings: ISettingMaster[]
) => {
  const returnSettings: Partial<Record<keyof T, ISettingMaster>> = {};
  for (const key in settingsDefinitions) {
    if (hasOwn(settingsDefinitions, key)) {
      returnSettings[key] = filterSetting(
        settingsDefinitions[key].domain,
        settingsDefinitions[key].settingType,
        resultInitSettings
      );
    }
  }
  return returnSettings;
};

export const buildQueryGlobalSettings = (settingsDefinitions: ISettingDefinistion) => {
  const domains = new Set<string>();
  for (const key in settingsDefinitions) {
    if (hasOwn(settingsDefinitions, key)) {
      domains.add(settingsDefinitions[key].domain);
    }
  }

  const settingTypes = new Set<string>();
  for (const key in settingsDefinitions) {
    if (hasOwn(settingsDefinitions, key)) {
      settingTypes.add(settingsDefinitions[key].settingType);
    }
  }

  return {
    domain: [...domains],
    settingType: [...settingTypes],
  };
};
